<template>
  <div id="audienceSegments">
    <div
      v-if="!isLoading"
      class="selection">
      <template v-if="!dataProvider">
        <div class="separator">
          <p>{{ i18n.t('messages.selectDataProvider') }}</p>
        </div>
        <div class="content">
          <selectable-tiles
            :items="dataMarketplaceProviders"
            :should-display-labels="true"
            label-property-name="name"
            :selected-items="[dataProvider]"
            :is-multiselect="false"
            :allow-empty="true"
            tile-width="16rem"
            tile-padding="2rem"
            @selection-changed="dataProviderSelected" />
        </div>
      </template>
      <template v-else-if="!dataMarketplace && !selectedSegmentType">
        <div class="head">
          <div class="input-container">
            <div class="icon">
              <icon-search />
            </div>
            <input
              v-model="searchTerm"
              name="segmentSearch"
              class="form-control table-text-filter"
              type="text"
              :placeholder="capitalize(i18n.t('actions.searchForCategoriesAndSegments'))" />
            <div class="delete">
              <div
                v-if="searchTerm != null && searchTerm.length > 0"
                @click="clearSearchTerm">
                <icon-circle-x />
              </div>
            </div>
          </div>
        </div>

        <div class="separator">
          <p>{{ i18n.t(`messages.${searchTerm ? 'resultsForCategories' : 'exploreByCategory'}`) }}</p>
        </div>

        <div class="content">
          <div
            v-if="!selectedSegmentType"
            class="cards"
            :class="!!searchTerm ? 'small' : ''">
            <external-segment-type-card
              v-for="segment in filteredSegmentTypes"
              :key="segment.id"
              :segment="segment"
              :small="!!searchTerm"
              @segment-type-selected="segmentTypeSelected" />
          </div>

          <!-- MESSAGE WHEN SEARCH RETURNS NO RESPONSE-->
          <div
            v-if="filteredSegmentTypes.length === 0"
            class="empty">
            <icon-colored-sad />
            <p>{{ i18n.t('messages.noCategoriesMatchYourSearch') }}</p>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="back">
          <!-- Can't switch data provider if we have other segment rules -->
          <button
            v-if="!dataMarketplace || !hasSegmentSiblings"
            class="btn btn-secondary btn-sm"
            @click="dataMarketplace ? dataProviderSelected([null]) : segmentTypeSelected(null)">
            &#8249; {{ i18n.t(`messages.${dataMarketplace ? 'backToDataProviders' : 'backToCategories'}`) }}
          </button>
        </div>

        <div
          v-if="!selectedSegment"
          class="content no-line">
          <external-segment-table
            id="AudienceSegmentTable_list_table"
            :segment-type="selectedSegmentType"
            :data-provider="dataProvider"
            @select-audience-segment="segmentSelected" />
        </div>

        <div
          v-if="!isLoading && hasNoSegments"
          class="empty">
          <icon-colored-sad />
          {{ i18n.t('messages.noPredefinedSegments') }}
        </div>
      </template>
    </div>

    <loader
      v-if="isLoading"
      :style-object="loaderStyle" />

    <div
      v-if="searchTerm"
      class="selection">
      <div class="separator">
        <p>{{ i18n.t(`messages.${searchTerm ? 'resultsForSegments' : 'exploreBySegment'}`) }}</p>
      </div>

      <div class="content">
        <external-segment-table
          id="AudienceSegmentTable_list_table"
          :key="debouncedSearchTerm"
          :show-category-col="true"
          :search-term="debouncedSearchTerm"
          :data-provider="dataProvider"
          @select-audience-segment="segmentSelected" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { Loader } from '@/common/components';
  import { SelectableTiles } from '@/common/components/controls';
  import { AudienceRuleModes, isExternalSegmentRule } from '@/common/enums/audience-rule-modes';
  import { ExternalSegmentTypes } from '@/common/enums/external-segments/external-segment-types.enum';
  import { IconColoredSad } from '@/common/icons/colored';
  import { IconCircleX, IconSearch } from '@/common/icons/monochrome';
  import { AudienceLogic, DataProvider, FilterCollection, QueryParams } from '@/common/models';
  import AudienceRule from '@/common/models/audience/AudienceRule';
  import AudienceRuleCustomGroup from '@/common/models/audience/AudienceRuleCustomGroup';
  import ExternalSegment from '@/common/models/audience/ExternalSegment';
  import ExternalSegmentType from '@/common/models/audience/ExternalSegmentType';
  import { useInjected } from '@/globals';
  import ExternalSegmentTable from '@/org-app/components/audienceRules/rules/externalSegment/ExternalSegmentTable.vue';
  import ExternalSegmentTypeCard from '@/org-app/components/audienceRules/rules/externalSegment/ExternalSegmentTypeCard.vue';
  import { useDataMarketplaces } from '@/org-app/services/tanstack/queries';
  import { useDataProviders } from '@/org-app/services/tanstack/queries/dataProviders.queries';
  import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';
  import LocalCache from '@/utils/LocalCache';
  import QuickMathUtils from '@/utils/QuickMathUtils';
  import { mapActions, mapOrgAppActions } from '@/utils/StateMapping';

  // Setup
  const props = defineProps<{
    audienceRule: AudienceRule;
    mode: AudienceRuleModes;
  }>();
  const emits = defineEmits<{
    (e: 'onExternalSegmentSelected', group: AudienceRuleCustomGroup): void;
  }>();
  const { updateAudienceRuleName, updateAudienceRuleSegments, updateAudienceRuleDataProviderID } = mapActions();
  const { getAudienceRuleCustomGroup, getExternalSegmentsByOrg } = mapOrgAppActions();
  const organization = LocalCache.get('organization');
  const audienceLogic = useInjected<AudienceLogic>('audienceLogic');
  const { items: dataProviders } = useDataProviders();
  const { items: dataMarketplaces } = useDataMarketplaces();
  const i18n = useI18n();

  const dataProvider = computed(() => {
    return dataProviders.value.find((dp) => dp.id === audienceLogic.value.dataProviderID);
  });
  const dataMarketplace = computed(() =>
    dataMarketplaces.value.find((dm) => dm.id === audienceLogic.value.dataMarketplaceID),
  );
  const dataMarketplaceProviders = computed(() => {
    if (dataMarketplace.value) {
      return dataProviders.value.filter((dp) => dp.dataMarketplaceID === dataMarketplace.value.id);
    } else {
      return [];
    }
  });
  const hasSegmentSiblings = computed(() => {
    return audienceLogic.value.audienceRules.filter((r) => isExternalSegmentRule(r.mode)).length > 1;
  });

  // Constant
  const loaderStyle = {
    position: 'absolute',
    backgroundColor: 'transparent',
  };

  // Computed
  const filteredSegmentTypes = computed(() => {
    if (!segmentTypes.value?.length) return [];
    const types = segmentTypes.value.filter((type) => type.total > 0);
    if (searchTerm.value === null) return types;
    return types.filter(
      (type) => stringIncludes(type.name, searchTerm.value) || stringIncludes(type.description, searchTerm.value),
    );
  });
  const hasNoSegments = computed(() => {
    if (!segmentTypes.value?.length) return true;
    return QuickMathUtils.sum(segmentTypes.value.map((t) => t.total)) === 0;
  });
  const searchTerm = computed({
    get: () => debouncedSearchTerm.value,
    set(val) {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        debouncedSearchTerm.value = val;
      }, 500);
    },
  });

  // Reactive data
  let debounceTimeout = null;
  const isLoading = ref(true);
  const debouncedSearchTerm = ref<string>(null);
  const segmentTypes = ref<ExternalSegmentType[]>([]);
  const selectedSegmentType = ref<ExternalSegmentType>(null);
  const selectedSegment = ref<ExternalSegment>(null);

  // Helpers
  function clearSearchTerm() {
    searchTerm.value = null;
  }
  function segmentTypeSelected(segment: ExternalSegmentType) {
    searchTerm.value = null;
    selectedSegmentType.value = segment;
  }
  function stringIncludes(str: string, searchTerm: string) {
    return str?.toLowerCase().includes(searchTerm?.toLowerCase());
  }
  function segmentSelected(segment: ExternalSegment) {
    searchTerm.value = null;
    if (!selectedSegmentType.value) {
      selectedSegmentType.value = segmentTypes.value.find((type) => type.grouping.indexOf(segment.category) > -1);
    }
    selectedSegment.value = segment;

    const audienceRuleCustomGroup = new AudienceRuleCustomGroup(
      segment.name,
      segment.s3Path,
      organization.id,
      segment.externalSegmentId,
    );
    updateAudienceRuleName({
      audienceRule: props.audienceRule,
      value: segment.name,
    });
    updateAudienceRuleSegments({
      audienceRule: props.audienceRule,
      value: [segment],
    });
    emits('onExternalSegmentSelected', audienceRuleCustomGroup);
  }
  function dataProviderSelected([dp]: DataProvider[]) {
    audienceLogic.value.dataProviderID = dp?.id;
    updateAudienceRuleDataProviderID({
      audienceRule: props.audienceRule,
      value: dp?.id,
    });
  }

  // Data fetching
  async function fetchExternalSegmentTypes() {
    // early return
    if (!dataProvider.value) {
      segmentTypes.value = [];
      return;
    }
    // we are checking that there is content on the backend for each category
    const dataProviderId = dataProvider.value.id;
    const qp = QueryParams.all(new FilterCollection([AudienceSegmentUtils.enabledFilter]), ['category']);
    qp.customQueryParams = { dataProviderId };
    const segmentCategories = await qp.run(getExternalSegmentsByOrg);
    const segmentCounts = segmentCategories.items.reduce(
      (result, item) => {
        const category = AudienceSegmentUtils.audienceSegmentTypeBEToFE(item.category);
        if (!result[category]) {
          result[category] = 0;
        }
        result[category] += 1;
        return result;
      },
      {} as Record<ExternalSegmentTypes, number>,
    );

    segmentTypes.value = AudienceSegmentUtils.audienceSegmentTypes()
      .filter((type) => !!segmentCounts[type.id])
      .map((type) =>
        ExternalSegmentType.fromObject({
          ...type,
          total: segmentCounts[type.id],
        }),
      );
  }

  watch(dataProvider, fetchExternalSegmentTypes, { immediate: true });
  onMounted(async () => {
    if (props.audienceRule?.id !== -1 || props.audienceRule?.audienceRuleCustomGroup) {
      if (props.audienceRule.id !== -1) {
        // eslint-disable-next-line vue/no-mutating-props
        props.audienceRule.audienceRuleCustomGroup = await getAudienceRuleCustomGroup({
          id: props.audienceRule.customGroupID,
        });
      }
    }
    isLoading.value = false;
  });
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  #audienceSegments {
    display: flex;
    flex-direction: column;
    position: relative;

    .selected {
      display: flex;
      flex-direction: column;

      .reset {
        margin-top: 2rem;
      }
    }

    .selection {
      display: flex;
      flex-direction: column;
      position: relative;

      .head {
        display: flex;
        gap: 2rem;
        flex-direction: column;

        .input-container {
          position: relative;
          max-width: 25rem;

          input {
            outline: none;
          }

          .delete {
            display: flex;
            align-items: center;
            position: absolute;
            right: 0.1rem;
            top: 0.9rem;
            margin-right: 0.6rem;

            div {
              display: flex;
              cursor: pointer;
            }

            svg {
              fill: #000;
              stroke: #000;
              opacity: 0.2;
              width: 1.6rem !important;
              height: 1.6rem !important;
            }
          }
        }
      }

      .separator {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        margin-top: 2rem;

        &.back {
          justify-content: flex-start;

          p {
            cursor: pointer;
          }
        }

        p {
          position: relative;
          color: $lift-blue;
          text-transform: uppercase;
          font-weight: bold;
          margin: 0;
          background: white;
          padding: 0 1rem;
        }
      }

      .content {
        border-top: 1px solid $borders-dark;
        margin-top: -1rem;
        padding-top: 2rem;
        padding-bottom: 2rem;

        &.no-line {
          border-top: none;
        }

        .cards {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          grid-gap: 2rem;
        }
      }
    }

    .empty {
      display: flex;
      flex-direction: row;

      svg {
        height: 2rem;
        margin-right: 1rem;
      }
    }
  }

  @media (max-width: 768px) {
    #audienceSegments .selection .content .cards {
      grid-template-columns: repeat(1, minmax(0, 1fr));

      &.small {
        grid-template-columns: repeat(1, 1fr);
      }
    }
  }

  @media (min-width: 768px) {
    #audienceSegments .selection .content .cards {
      grid-template-columns: repeat(1, minmax(0, 1fr));

      &.small {
        grid-template-columns: repeat(1, minmax(0, 1fr));
      }
    }
  }

  @media (min-width: 1000px) {
    #audienceSegments .selection .content .cards {
      grid-template-columns: repeat(2, minmax(0, 1fr));

      &.small {
        grid-template-columns: repeat(2, 1fr);
      }
    }
  }

  @media (min-width: 1250px) {
    #audienceSegments .selection .content .cards {
      grid-template-columns: repeat(3, minmax(0, 1fr));

      &.small {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }
  }

  @media (min-width: 1500px) {
    #audienceSegments .selection .content .cards {
      grid-template-columns: repeat(6, minmax(0, 1fr));

      &.small {
        grid-template-columns: repeat(6, minmax(0, 1fr));
      }
    }
  }

  @media (min-width: 2000px) {
    #audienceSegments .selection .content .cards {
      grid-template-columns: repeat(6, minmax(0, 1fr));

      &.small {
        grid-template-columns: repeat(6, minmax(0, 1fr));
      }
    }
  }

  @media (min-width: 2500px) {
    #audienceSegments .has-data .selection .content .cards {
      grid-template-columns: repeat(6, minmax(0, 1fr));

      &.small {
        grid-template-columns: repeat(6, minmax(0, 1fr));
      }
    }
  }
</style>
